import { z } from 'zod';
import { IMAGE_CATEGORIES, IMAGE_OCR_STATUSES } from '../constants.js';
import { cropMetadataSchema } from '../schemas.js';
import { DRAFT_FIELDS } from '../aggregation/types.js';
import { PACKAGE_SAVE_STATUSES } from './types.js';

const draftFieldSchema = z.enum(DRAFT_FIELDS);

/** Lightweight AggregatedField validation for commit payloads. */
const fieldCandidateSchema = z.object({
  id: z.string().min(1),
  field: draftFieldSchema,
  value: z.union([z.string(), z.number()]),
  normalizedValue: z.union([z.string(), z.number()]).optional(),
  rawSourceText: z.string().optional(),
  confidence: z.number().nullable().optional(),
  confidenceCategory: z.enum(['high', 'review', 'uncertain']),
  sourceImageLocalId: z.string().min(1),
  sourceImageCategory: z.enum(IMAGE_CATEGORIES),
  sourceVariant: z.string().optional(),
  warnings: z.array(z.string()),
  scoreReason: z.string().optional(),
  score: z.number().optional(),
});

const aggregatedFieldSchema = z.object({
  field: draftFieldSchema,
  candidates: z.array(fieldCandidateSchema),
  recommendedCandidateId: z.string().optional(),
  recommendedReason: z.string().optional(),
  selectedCandidateId: z.string().optional(),
  manualValue: z.union([z.string(), z.number()]).optional(),
  status: z.enum(['missing', 'suggested', 'conflict', 'confirmed', 'manual']),
  blockingConflict: z.boolean(),
  warnings: z.array(z.string()),
});

export const packageDraftReviewSchema = z.object({
  fields: z.array(aggregatedFieldSchema),
  selectedCandidateIds: z.record(z.string()).default({}),
  manualValues: z.record(z.union([z.string(), z.number()])).default({}),
  confirmedFields: z.array(draftFieldSchema).default([]),
  fieldSources: z.record(z.unknown()).default({}),
  conflicts: z.array(draftFieldSchema).default([]),
  reviewStatus: z.enum([
    'not_started',
    'in_progress',
    'ready_for_phase4',
    'blocked',
  ]),
  explicitManualEntry: z.boolean().default(false),
});

export const commitImageDescriptorSchema = z.object({
  clientImageId: z.string().min(1).max(80),
  category: z.enum(IMAGE_CATEGORIES),
  sortOrder: z.number().int().min(0).max(1000),
  isPrimary: z.boolean().optional().default(false),
  originalFilename: z.string().max(500).nullable().optional(),
  cropMetadata: cropMetadataSchema.nullable().optional(),
  ocrText: z.string().max(20000).nullable().optional(),
  ocrStatus: z.enum(IMAGE_OCR_STATUSES).optional(),
  scanMode: z.string().max(40).optional(),
  strategy: z.string().max(40).optional(),
});

export const packageDraftCommitSchema = z.object({
  clientOperationId: z.string().uuid(),
  mode: z.enum(['package', 'sticky_note']),
  review: packageDraftReviewSchema,
  images: z.array(commitImageDescriptorSchema).min(1).max(20),
});

export type PackageDraftCommitInput = z.infer<typeof packageDraftCommitSchema>;

export const packageSaveStatusSchema = z.enum(PACKAGE_SAVE_STATUSES);
